import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { connect } from 'redux-zero/react';
import { combineActions } from 'redux-zero/utils';
import { PageHeader, PageTitle } from '@tokenyico/tokeny-ui';
import {
  DsExplorerLink,
  DsInputString,
  DsLogo,
  DsSelectCountry,
  DsSelectSimple,
  DsInputDate,
  DsForm,
  DsButton,
} from '@tokenyico/design-system/dist/reactJS';

import { TypeOfInvestorIcon } from 'app/helpers/investor';
import { formActions, holderActions } from 'app/state/actions';
import { IdentityService } from 'services';
import occupations from 'assets/data/occupations-activities.json';
import { vRequired } from '@tokenyico/design-system';
import { isEqual } from 'lodash';

const InstitutionalFormEdit = ({ investor, values, setValues, wallet, selectedToken }) => {
  const { t } = useTranslation();

  return (
    <ul className="investor-details">
      <li className="investor-details__group-information">
        <div className="investor-details__title">{t('shared.tradeName')}</div>
        <div>
          <DsInputString
            name="institutionTradeName"
            value={values.institutionTradeName}
            onDsInput={({ detail }) => setValues({ ...values, institutionTradeName: detail })}
            isMini
            validate={vRequired}
          />
        </div>
      </li>
      <li className="investor-details__group-information">
        <div className="investor-details__title">{t('shared.legalName')}</div>
        <div>
          <DsInputString
            name="institutionLegalName"
            value={values.institutionLegalName}
            onDsInput={({ detail }) => setValues({ ...values, institutionLegalName: detail })}
            isMini
            validate={vRequired}
          />
        </div>
      </li>
      <li className="investor-details__group-information">
        <div className="investor-details__title">{t('shared.email')}</div>
        <div>{investor.email}</div>
      </li>
      <li className="investor-details__group-information">
        <div className="investor-details__title">
          <DsLogo name="onchainid" style={{ width: '6rem' }} />
        </div>
        <div>
          <DsExplorerLink
            address={investor.identityAddress}
            network={selectedToken.blockchainNetwork}
            type="onchainid"
          />
        </div>
      </li>
      <li className="investor-details__group-information">
        <div className="investor-details__title">
          {wallet.provider === 'ONCHAINID' && <DsLogo name="onchainid" style={{ width: '6rem' }} />}
          {t('shared.walletAddress')}
        </div>
        <div>
          <DsExplorerLink address={wallet.wallet} network={selectedToken.blockchainNetwork} />
        </div>
      </li>
      <li className="investor-details__group-information">
        <div className="investor-details__title">{t('shared.typeOfInvestor')}</div>
        <div style={{ textTransform: 'capitalize' }}>
          <span className="_p:right(2)">
            <TypeOfInvestorIcon investor={investor} size={12} />
          </span>
          {investor.typeOfInvestor}
        </div>
      </li>
      <li className="investor-details__group-information">
        <div className="investor-details__title">{t('shared.registryNumber')}</div>
        <div>
          <DsInputString
            name="institutionRegistryNumber"
            value={values.institutionRegistryNumber}
            onDsInput={({ detail }) => setValues({ ...values, institutionRegistryNumber: detail })}
            isMini
            validate={vRequired}
          />
        </div>
      </li>
      <li className="investor-details__group-information">
        <div className="investor-details__title">{t('shared.registryCountry')}</div>
        <div>
          <DsSelectCountry
            name="institutionRegistryCountry"
            returnType="alpha3"
            value={values.institutionRegistryCountry}
            onDsInput={({ detail }) => setValues({ ...values, institutionRegistryCountry: detail })}
            isRequired
            isMini
          />
        </div>
      </li>
      <li className="investor-details__group-information">
        <div className="investor-details__title">{t('shared.activity')}</div>
        <div>
          <DsSelectSimple
            name="institutionActivity"
            value={values.institutionActivity}
            onDsInput={({ detail }) => setValues({ ...values, institutionActivity: detail })}
            options={occupations.map((o) => ({ label: o.name, value: o.name }))}
            validate={vRequired}
            isMini
          />
        </div>
      </li>
      <li className="investor-details__group-information">
        <div className="investor-details__title">{t('shared.incorporationDate')}</div>
        <div>
          <DsInputDate
            name="institutionIncorporationDate"
            value={
              values.institutionIncorporationDate
                ? values.institutionIncorporationDate.split('T')[0]
                : null
            }
            onDsInput={({ detail }) =>
              setValues({
                ...values,
                institutionIncorporationDate: detail ? detail + 'T00:00:00Z' : detail,
              })
            }
            isRequired
            isMini
          />
        </div>
      </li>
      <li className="investor-details__group-information">
        <div className="investor-details__title">{t('shared.legalForm')}</div>
        <div>
          <DsInputString
            name="institutionLegalForm"
            value={values.institutionLegalForm}
            onDsInput={({ detail }) => setValues({ ...values, institutionLegalForm: detail })}
            isMini
            validate={vRequired}
          />
        </div>
      </li>
      <li className="investor-details__group-information">
        <div className="investor-details__title">{t('shared.countryOfIncorporation')}</div>
        <div>
          <DsSelectCountry
            name="institutionCountry"
            returnType="alpha3"
            value={values.institutionCountry}
            onDsInput={({ detail }) => setValues({ ...values, institutionCountry: detail })}
            isRequired
            isMini
          />
        </div>
      </li>
      <li className="investor-details__group-information">
        <div className="investor-details__title">{t('shared.city')}</div>
        <div>
          <DsInputString
            name="institutionCity"
            value={values.institutionCity}
            onDsInput={({ detail }) => setValues({ ...values, institutionCity: detail })}
            isMini
            validate={vRequired}
          />
        </div>
      </li>
      <li className="investor-details__group-information">
        <div className="investor-details__title">{t('shared.street')}</div>
        <div>
          <DsInputString
            name="institutionStreet"
            value={values.institutionStreet}
            onDsInput={({ detail }) => setValues({ ...values, institutionStreet: detail })}
            isMini
            validate={vRequired}
          />
        </div>
      </li>
      <li className="investor-details__group-information">
        <div className="investor-details__title">{t('shared.buildingNumber')}</div>
        <div>
          <DsInputString
            name="institutionBuildingNumber"
            value={values.institutionBuildingNumber}
            onDsInput={({ detail }) => setValues({ ...values, institutionBuildingNumber: detail })}
            isMini
            validate={vRequired}
          />
        </div>
      </li>
      <li className="investor-details__group-information">
        <div className="investor-details__title">{t('shared.state')}</div>
        <div>
          <DsInputString
            name="institutionState"
            value={values.institutionState}
            onDsInput={({ detail }) => setValues({ ...values, institutionState: detail })}
            isMini
            validate={vRequired}
          />
        </div>
      </li>
      <li className="investor-details__group-information">
        <div className="investor-details__title">{t('shared.zipCode')}</div>
        <div>
          <DsInputString
            name="institutionZipCode"
            value={values.institutionZipCode}
            onDsInput={({ detail }) => setValues({ ...values, institutionZipCode: detail })}
            isMini
            validate={vRequired}
          />
        </div>
      </li>
    </ul>
  );
};

const IndividualFormEdit = ({ investor, values, setValues, wallet, selectedToken }) => {
  const { t } = useTranslation();
  return (
    <ul className="investor-details">
      <li className="investor-details__group-information">
        <div className="investor-details__title">{t('shared.firstName')}</div>
        <div>
          <DsInputString
            name="firstName"
            value={values.firstName}
            onDsInput={({ detail }) => setValues({ ...values, firstName: detail })}
            validate={vRequired}
            isMini
          />
        </div>
      </li>
      <li className="investor-details__group-information">
        <div className="investor-details__title">{t('shared.lastName')}</div>
        <div>
          <DsInputString
            name="lastName"
            value={values.lastName}
            onDsInput={({ detail }) => setValues({ ...values, lastName: detail })}
            validate={vRequired}
            isMini
          />
        </div>
      </li>
      <li className="investor-details__group-information">
        <div className="investor-details__title">{t('shared.email')}</div>
        <div>{investor.email}</div>
      </li>
      <li className="investor-details__group-information">
        <div className="investor-details__title">
          <DsLogo name="onchainid" style={{ width: '6rem' }} />
        </div>
        <div>
          <DsExplorerLink
            address={investor.identityAddress}
            network={selectedToken.blockchainNetwork}
            type="onchainid"
          />
        </div>
      </li>
      <li className="investor-details__group-information">
        <div className="investor-details__title">
          {wallet.provider === 'ONCHAINID' && <DsLogo name="onchainid" style={{ width: '6rem' }} />}
          {t('shared.walletAddress')}
        </div>
        <div>
          <DsExplorerLink address={wallet.wallet} network={selectedToken.blockchainNetwork} />
        </div>
      </li>
      <li className="investor-details__group-information">
        <div className="investor-details__title">{t('shared.typeOfInvestor')}</div>
        <div style={{ textTransform: 'capitalize' }}>
          <span className="_p:right(2)">
            <TypeOfInvestorIcon investor={investor} size={12} />
          </span>
          {investor.typeOfInvestor}
        </div>
      </li>
      <li className="investor-details__group-information">
        <div className="investor-details__title">{t('shared.gender')}</div>
        <div>
          <DsSelectSimple
            name="gender"
            value={values.gender}
            onDsInput={({ detail }) => setValues({ ...values, gender: detail })}
            options={[
              { label: t('shared.male'), value: 'Male' },
              { label: t('shared.female'), value: 'Female' },
            ]}
            isMini
          />
        </div>
      </li>
      <li className="investor-details__group-information">
        <div className="investor-details__title">{t('shared.dateOfBirth')}</div>
        <div>
          <DsInputDate
            name="birthday"
            value={values.birthday ? values.birthday.split('T')[0] : null}
            onDsInput={({ detail }) =>
              setValues({ ...values, birthday: detail ? detail + 'T00:00:00Z' : detail })
            }
            isRequired
            isMini
          />
        </div>
      </li>
      <li className="investor-details__group-information">
        <div className="investor-details__title">{t('shared.birthplace')}</div>
        <div>
          <DsInputString
            name="birthplace"
            value={values.birthplace}
            onDsInput={({ detail }) => setValues({ ...values, birthplace: detail })}
            validate={vRequired}
            isMini
          />
        </div>
      </li>
      <li className="investor-details__group-information">
        <div className="investor-details__title">{t('shared.nationality')}</div>
        <div>
          <DsSelectCountry
            name="nationality"
            returnType="alpha3"
            value={values.nationality}
            onDsInput={({ detail }) => setValues({ ...values, nationality: detail })}
            isRequired
            isMini
          />
        </div>
      </li>
      <li className="investor-details__group-information">
        <div className="investor-details__title">{t('shared.occupationalSector')}</div>
        <div>
          <DsSelectSimple
            name="occupation"
            value={values.occupation}
            onDsInput={({ detail }) => setValues({ ...values, occupation: detail })}
            options={occupations.map((o) => ({ label: o.name, value: o.name }))}
            validate={vRequired}
            isMini
          />
        </div>
      </li>
      <li className="investor-details__group-information">
        <div className="investor-details__title">{t('shared.countryOfResidence')}</div>
        <div>
          <DsSelectCountry
            name="country"
            returnType="alpha3"
            value={values.country}
            onDsInput={({ detail }) => setValues({ ...values, country: detail })}
            isRequired
            isMini
          />
        </div>
      </li>
      <li className="investor-details__group-information">
        <div className="investor-details__title">{t('shared.state')}</div>
        <div>
          <DsInputString
            name="state"
            value={values.state}
            onDsInput={({ detail }) => setValues({ ...values, state: detail })}
            validate={vRequired}
            isMini
          />
        </div>
      </li>
      <li className="investor-details__group-information">
        <div className="investor-details__title">{t('shared.city')}</div>
        <div>
          <DsInputString
            name="city"
            value={values.city}
            onDsInput={({ detail }) => setValues({ ...values, city: detail })}
            validate={vRequired}
            isMini
          />
        </div>
      </li>
      <li className="investor-details__group-information">
        <div className="investor-details__title">{t('shared.street')}</div>
        <div>
          <DsInputString
            name="street"
            value={values.street}
            onDsInput={({ detail }) => setValues({ ...values, street: detail })}
            validate={vRequired}
            isMini
          />
        </div>
      </li>
      <li className="investor-details__group-information">
        <div className="investor-details__title">{t('shared.buildingNumber')}</div>
        <div>
          <DsInputString
            name="buildingNumber"
            value={values.buildingNumber}
            onDsInput={({ detail }) => setValues({ ...values, buildingNumber: detail })}
            validate={vRequired}
            isMini
          />
        </div>
      </li>
      <li className="investor-details__group-information">
        <div className="investor-details__title">{t('shared.zipCode')}</div>
        <div>
          <DsInputString
            name="zipCode"
            value={values.zipCode}
            onDsInput={({ detail }) => setValues({ ...values, zipCode: detail })}
            validate={vRequired}
            isMini
          />
        </div>
      </li>
      {investor.individual.passportNumber && (
        <li className="investor-details__group-information">
          <div className="investor-details__title">{t('shared.passportNumber')}</div>
          <div>
            <DsInputString
              name="passportNumber"
              value={values.passportNumber}
              onDsInput={({ detail }) => setValues({ ...values, passportNumber: detail })}
              validate={vRequired}
              isMini
            />
          </div>
        </li>
      )}
      {investor.individual.idNumber && (
        <li className="investor-details__group-information">
          <div className="investor-details__title">{t('shared.idNumber')}</div>
          <div>
            <DsInputString
              name="idNumber"
              value={values.idNumber}
              onDsInput={({ detail }) => setValues({ ...values, idNumber: detail })}
              validate={vRequired}
              isMini
            />
          </div>
        </li>
      )}
      <li className="investor-details__group-information">
        <div className="investor-details__title">{t('shared.phoneNumber')}</div>
        <div>
          <DsInputString
            name="phone"
            value={values.phone}
            onDsInput={({ detail }) => setValues({ ...values, phone: detail })}
            validate={vRequired}
            isMini
          />
        </div>
      </li>
    </ul>
  );
};

const ProfileBasicInfoEdit = ({ history, user, selectedToken, setError, setSuccess, setUser }) => {
  const { t } = useTranslation();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [values, setFormValues] = useState(
    user.typeOfInvestor === 'individual' ? { ...user.individual } : { ...user.institutional },
  );
  const [initialValues, setInitialValues] = useState(null);
  const [isChanged, setIsChanged] = useState(false);

  useEffect(() => {
    setInitialValues(values);
  }, [isSubmitting]);

  const setValues = (editedValues) => {
    setIsChanged(!isEqual(initialValues, editedValues));
    setFormValues(editedValues);
  };

  const wallet = user.walletsAndStatus.find(
    (w) => w.identityRegistryStorage === selectedToken.identityRegistryStorage,
  );

  async function onSubmit() {
    try {
      setIsSubmitting(true);
      await IdentityService.updateIdentity(selectedToken.id, user.typeOfInvestor, values);
      setSuccess(t('shared.operationSuccessful'));
      history.push('/profile/basic-info');
      setUser({
        ...user,
        [user.typeOfInvestor === 'individual' ? 'individual' : 'institutional']: values,
      });
    } catch (error) {
      setError(error);
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <div className="basic-info sidebar _m:left(2)">
      <DsForm onSubmit={onSubmit}>
        <PageHeader>
          <PageTitle>
            <div className="_text(700) _text:xl _m:bottom(2)">{t('shared.basicInfo')}</div>
            <div>
              <DsButton
                version="monochrome"
                label={t('shared.cancel')}
                type="button"
                onClick={() => history.push('/profile/basic-info')}
                className="_mright-2"
                isDisabled={isSubmitting}
              />

              <DsButton
                version="primary"
                label={t('shared.save')}
                type="submit"
                icon="checkmark"
                isReverse
                isLoading={isSubmitting}
                isDisabled={!isChanged}
              />
            </div>
          </PageTitle>
        </PageHeader>
        <div>
          {user.typeOfInvestor === 'institution' ? (
            <InstitutionalFormEdit
              investor={user}
              values={values}
              setValues={setValues}
              wallet={wallet}
              selectedToken={selectedToken}
            />
          ) : (
            <IndividualFormEdit
              investor={user}
              values={values}
              setValues={setValues}
              wallet={wallet}
              selectedToken={selectedToken}
            />
          )}
        </div>
      </DsForm>
    </div>
  );
};

const mapToProps = ({ holder: { user, selectedToken } }) => ({
  user,
  selectedToken,
});

export default connect(
  mapToProps,
  combineActions(formActions, holderActions),
)(ProfileBasicInfoEdit);
